/**
 * The `[n]` citation marker, shared by the markdown plugin that renders the
 * inline bubbles (remark-citations.ts) and the sources footer that lists what
 * the answer cites (message-sources.tsx), so both agree on what a citation is.
 * Exported as a pattern string, not a /g instance: a shared global regex
 * carries lastIndex between .test/.exec callers.
 */
export const CITATION_PATTERN = String.raw`\[(\d{1,3})\]`

const CITED_RE = new RegExp(CITATION_PATTERN, 'g')

/**
 * Det remark inte gör bubblor av, bortplockat ur rå markdown: kodblock
 * (också ett ostängt block under strömning, som remark läser till slutet),
 * kodspann och en riktig länk `[1](dest)`. Länken kräver en destination utan
 * blanksteg, precis som remark: `[1](se tabell 2)` förblir text och en
 * bubbla. En escapad `\[1\](url)` är ingen länk utan text, så den lämnas.
 *
 * Där regex och remark ändå skiljer sig ska felet bli en post för mycket
 * (en lucka i numreringen), aldrig en bortplockad bubbla: en bubbla utan
 * visat nummer döljs, och dess källa försvinner ur listan. Därför får ett
 * kodspann aldrig gå över en radbrytning (en ensam backtick parar annars ihop
 * sig med nästa styckes kodspann) och en stängande fence får vara längre än
 * den öppnande.
 */
const NON_BUBBLE_RES = [
	// Backtick-fence: en backtick i resten av raden gör den till kodspann.
	/^ {0,3}(`{3,})(?![^\n]*`)[\s\S]*?(?:^ {0,3}\1`*[ \t]*$|(?![\s\S]))/gm,
	/^ {0,3}(~{3,})[\s\S]*?(?:^ {0,3}\1~*[ \t]*$|(?![\s\S]))/gm,
	/(?<![\\`])(`+)(?!`)[^\n]*?(?<!`)\1(?!`)/g,
	/(?<!\\)\[\d{1,3}\]\([^\s()]*(?:\s+"[^"]*")?\)/g,
]

/** Ersätter varje spann utan bubbla (i ordningen ovan) med `replace(spann)`. */
function replaceNonBubbles(
	markdown: string,
	replace: (span: string) => string,
): string {
	return NON_BUBBLE_RES.reduce(
		(text, re) => text.replace(re, replace),
		markdown,
	)
}

/**
 * Unique citation numbers in raw markdown, in order of first appearance —
 * the `[n]` the bubbles render (IM-52): code blocks, code spans and real
 * links get no bubble and are not counted (a counted `[n]` without a bubble
 * would take a displayed number no bubble shows, a gap in 1, 2, 3). remark
 * unescapes `\[1\]` into the text "[1]" (a bubble), so the escapes are
 * removed before matching. Parity with the real remark pipeline for the
 * shapes the model writes is locked by citations.test.ts; rarer markdown
 * (indented code, reference definitions, odd link destinations) can still
 * give a gap.
 */
export function extractCitationNumbers(markdown: string): number[] {
	const text = replaceNonBubbles(markdown, () => ' ').replace(
		/\\([[\]])/g,
		'$1',
	)
	const seen = new Set<number>()
	for (const m of text.matchAll(CITED_RE)) seen.add(Number(m[1]))
	return [...seen]
}

/**
 * Vilket nummer varje citerad källa VISAS med (IM-52, samma regel som sajtens
 * AI-ruta): originalnumret (index+1 i källramen, det modellen skriver) ->
 * 1, 2, 3 … i den ordning källorna först citeras. `[5] … [1] … [5]` visas
 * som 1, 2, 1.
 *
 * Stabilt under strömning: texten växer bara i slutet, så ett citat som fått
 * ett nummer behåller det och nya citat får nästa lediga. Undantaget är ett
 * `[n]` i ett kodspann eller en länk som ännu inte stängts: det räknas tills
 * stängningen kommer, precis som remark visar en bubbla dit. Uppslaget mot
 * källan görs alltid med ORIGINALnumret, det visade är bara en etikett — en
 * siffra kan därför inte hamna på fel källa. Nummer utanför ramen får inget
 * visat nummer (ingen bubbla).
 */
export function citationNumbering(
	markdown: string,
	sourceCount: number,
): Map<number, number> {
	const numbering = new Map<number, number>()
	for (const n of extractCitationNumbers(markdown)) {
		if (n >= 1 && n <= sourceCount) numbering.set(n, numbering.size + 1)
	}
	return numbering
}

/**
 * Skriver om `[n]` till de visade numren, i ETT pass (en ersättning per
 * träff), så att `[1]`→`[2]` och `[2]`→`[1]` inte kan skriva över varandra.
 * För kopiera/exportera: kopian ska säga samma nummer som skärmen. Kod och
 * länkar (inga bubblor, se replaceNonBubbles) lämnas orörda, liksom nummer
 * utan visat nummer (utanför ramen).
 */
export function renumberCitations(
	markdown: string,
	numbering: Map<number, number>,
): string {
	// Spannen utan bubbla parkeras bakom platshållare i Unicodes privata
	// område (inget modellen skriver) och sätts tillbaka oförändrade.
	const kept: string[] = []
	const parked = replaceNonBubbles(markdown, span => {
		kept.push(span)
		return `\uE000${kept.length - 1}\uE001`
	})
	let out = parked.replace(
		/(\\?\[)(\d{1,3})(\\?\])/g,
		(match, open: string, n: string, close: string) => {
			const shown = numbering.get(Number(n))
			return shown === undefined ? match : `${open}${shown}${close}`
		},
	)
	// Ett senare spann kan innehålla ett tidigare parkerat (länk i kodspann).
	const PARKED = /\uE000(\d+)\uE001/g
	while (PARKED.test(out)) {
		out = out.replace(PARKED, (_, i: string) => kept[Number(i)]!)
	}
	return out
}
